"use client";

import React from "react";
import { Session } from "next-auth";
import { format, formatDistanceToNow } from "date-fns";
import { useNotifications } from "../NotificationProvider";
import { cn } from "~/utils/fontsConfig";
import {
    NOTIFICATION_COLORS,
    NOTIFICATION_TITLES,
    NOTIFICATION_ICONS,
} from "./constants";
import { NotificationType } from "@prisma/client";
import { Notifications } from "~/app/preferences/types";

type Props = {
    notification: Notifications[number];
    user?: Session["user"];
};

export const Notification = ({ notification, user }: Props) => {
    const { markNotificationAsRead } = useNotifications();

    const bgColor =
        NOTIFICATION_COLORS[notification.type as NotificationType] ||
        NOTIFICATION_COLORS[NotificationType.EVENT_ANNOUNCEMENT];
    const title =
        NOTIFICATION_TITLES[notification.type as NotificationType] ||
        NOTIFICATION_TITLES[NotificationType.EVENT_ANNOUNCEMENT];
    const icon =
        NOTIFICATION_ICONS[notification.type as NotificationType] ||
        NOTIFICATION_ICONS[NotificationType.EVENT_ANNOUNCEMENT];

    const formatTimeAgo = (dateString: string | Date): string => {
        const date = new Date(dateString);
        const now = new Date();

        const diffInMs = now.getTime() - date.getTime();
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

        if (diffInDays <= 1) {
            return formatDistanceToNow(date, { addSuffix: true });
        }

        if (date.getFullYear() === now.getFullYear()) {
            return format(date, "MMM d");
        }

        return format(date, "MMM d, yyyy");
    };

    const handleNotificationClick = async () => {
        if (!notification.isRead) {
            await markNotificationAsRead(notification.id);
        }

        if (notification.notificationActionUrl) {
            window.open(notification.notificationActionUrl, "_blank");
        }
    };

    return (
        <div className="w-full rounded-lg bg-zinc-800 p-4 py-2">
            <div className="flex items-start gap-3">
                <div
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: bgColor }}
                >
                    <span className="">{icon}</span>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                        <h4
                            className={cn(
                                "truncate pr-2 text-sm font-medium",
                                notification.isRead
                                    ? "text-zinc-400"
                                    : "text-white",
                            )}
                        >
                            {title}
                        </h4>
                    </div>
                    <p className="mt-1 text-xs text-zinc-400">
                        {notification.message}
                    </p>
                    <p className="mt-1 text-xs text-zinc-400">
                        {formatTimeAgo(notification.createdAt)}
                    </p>
                    {notification.notificationActionUrl && (
                        <div className="mt-4">
                            <button
                                onClick={handleNotificationClick}
                                className="text-xs text-gray-500 hover:text-white"
                            >
                                View details
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
